import {Injectable, Inject, PLATFORM_ID, inject} from '@angular/core';
import { Location, isPlatformServer } from '@angular/common';

import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { Router } from '@angular/router';
import {ApiCommunicationPref, ApiConfiguration, ApiMembership, ApiNewsletterOptins, ApiUser, CommunicationPrefsApi, UsersApi} from '../api';
import { Logger, Shell, Cookies, RequestParams, AppConfig, Redirection } from '../common';
import { LocalStorageService } from 'angular-2-local-storage';
import * as jwtDecode from 'jwt-decode';

import * as uuid from 'uuid/v4';
import { environment } from 'environments/environment';

declare var gtag;

@Injectable()
export class UserService {
    constructor(
        private router: Router,
        private apiConfig: ApiConfiguration,
        private localStorage: LocalStorageService,
        private shell: Shell,
        private location: Location,
        private cookies: Cookies,
        private requestParams: RequestParams,
        private appConfig: AppConfig,
        private redirection: Redirection,
        private userApi: UsersApi,

        @Inject(PLATFORM_ID)
        private platformId: any
    ) {
    }

    /**
     * vv CAUTION: MAKE SURE TO UPDATE BOTH VARIANTS
     *    *** AND UPDATE IT ON TYTACCOUNTS! ***
     */
    smsOptin = `
        Receive periodic messages regarding Operation Hope volunteer opportunities or
        alerts and updates from TYT. Reply STOP to quit. Msg and data rates may apply.
    `;
    smsAgreement = `
        By providing my mobile number, I agree to
        receive periodic messages regarding Operation Hope volunteer opportunities or
        alerts and updates from TYT. Reply STOP to quit. Msg and data rates may apply.
    `;
    /**
     * ^^ CAUTION: MAKE SURE TO UPDATE BOTH VARIANTS
     *    *** AND UPDATE IT ON TYTACCOUNTS! ***
     */

    private commPrefsApi = inject(CommunicationPrefsApi);

    _user: ApiUser;

    public firstTime: boolean = true;

    userChanged: ReplaySubject<ApiUser> = new ReplaySubject(1);
    sourceChanged: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    get token(): string {
        return <string>this.apiConfig.accessToken;
    }

    set token(value: string) {
        this.apiConfig.accessToken = value;
    }

    get sourceFirstTime(): boolean {
        return <boolean>this.localStorage.get('trafficSourceFirstTime');
    }

    decodeToken(token): any {
        return jwtDecode(token);
    }

    async refreshToken() {

        if (!this._user || !this.token)
            return;

        let tokenStr = this.token
        let token = this.decodeToken(tokenStr);

        // Determine the appropriate renewal period before expiry

        let threshold = 60 * 60 * 8;

        if (token.renewalTime)
            threshold = token.renewalTime;
        else if (token.lifetime)
            threshold = Math.min(threshold, Math.round(token.lifetime * 0.25));

        let renewalTime = token.exp - threshold;

        if ((new Date().getTime() / 1000) < renewalTime) {
            return;
        }

        let userAuthToken = await this.userApi.refreshAuthentication({
            rememberMe: token.rememberMe || false
        }).toPromise();

        token = this.decodeToken(userAuthToken.token);
        let timeMs = 1000 * (token.exp - token.renewalTime);

        if (userAuthToken.type === 'User')
            this.completeLogin(userAuthToken, userAuthToken.token, false);
        else if (userAuthToken.type === 'Guest') {
            this.token = userAuthToken.token;
            this.cookies.set(environment.auth.cookie, { token });
        }
    }

    async refreshTokenPeriodically() {

        await this.refreshToken();

        // Do not wait to refresh token for server-side rendering.
        // (Infinite loads)

        if (!isPlatformServer(this.platformId)) {
            setTimeout(() => {
                this.refreshTokenPeriodically();
            }, this._tokenRefreshInterval)
        }
    }

    private _tokenRefreshInterval: number = 1000 * 60 * 30;
    private _postLoginUrl: string = null;
    private _postLoginAction: Function = null;
    private _source: string = null;

    set postLoginUrl(value: string) {
        if (value != null)
            value = value.replace(/^\/+/, '');

        this._postLoginUrl = value;
        this._postLoginAction = null;
    }

    get postLoginUrl() {
        return this._postLoginUrl;
    }

    requireLogin() {
        if (!this.user) {
            this.goToLogin();
            return false;
        }

        return true;
    }

    afterLogin(action) {
        this._postLoginAction = action;
        this._postLoginUrl = null;
    }

    get isAssumed() {
        return !!(this.localStorage.get('originalToken'));
    }

    async assume(token) {
        if (!token)
            throw new Error("Cannot assume a user without an access token.");

        console.log(`Assuming user with token ${token}`);

        let originalToken = null;

        if (this.token)
            originalToken = this.token;

        // Store the previous authentication details

        this.token = token;

        let user: ApiUser;
        console.log(`Fetching the assumed user from API...`);
        user = <any>await this.userApi.getCurrentUser().toPromise();

        console.log(`User assumption: Success`);
        user['assumed'] = true;

        console.log(`User assumption: Completing login...`);
        this.completeLogin(user, token, false);

        console.log(`User assumption: Saving original token...`);
        this.localStorage.set('originalToken', originalToken);

        console.log(`Fetching the assumed user from API...`);
    }

    async switchBack() {
        let token = this.localStorage.get<string>('originalToken');
        this.localStorage.remove('originalToken');

        if (token) {
            this.token = token;

            try {
                this.user = <any>await this.userApi.getCurrentUser().toPromise();
            } catch (e) {
                if (e.json)
                    e = e.json();

                console.error('Failed while fetching original user account:');
                console.error(e);
                alert('An error occurred while switching back to your original account.')
            }

            if (this.user) {
                this.completeLogin(this.user, token, false);
                this.redirection.go('/settings/memberships');
            } else {
                this.logout();
                this.goToLogin();
            }
        } else {
            this.logout();
            this.goToLogin();
        }
    }

    private _initialized = false;
    async init() {
        if (this._initialized)
            return;
        this._initialized = true;

        if (this.localStorage.get('trafficSource')) {
            this.source = <string>this.localStorage.get('trafficSource');
        }

        let repeatVisitorCookie = this.cookies.get('repeatVisitor');
        if (repeatVisitorCookie) {
            this.firstTime = false;
            //Logger.info('User is a repeat visitor');
        } else {
            this.firstTime = true;
            this.localStorage.set('repeatVisitor', "1");
            this.cookies.set('repeatVisitor', 1, 1000 * 60 * 60 * 24 * 365 * 5);

            //Logger.info('User is a first time visitor');
        }

        // TODO: swap out with equivalent Location service (this.location)
        let token: string = null;

        if (typeof location !== 'undefined') {
            let args = this.requestParams.params;
            if (args.token) {
                Logger.log(`Storing passed token ${args.token}`);
                this.localStorage.set('authToken', args.token);
                this.cookies.set(environment.auth.cookie, { token: args.token });

                // Remove the token from the current URL to avoid
                // leaking tokens if the URL is shared

                let newUrl = location.href.replace(/token=[^&]+/, 'tx=');
                history.replaceState({}, document.title, newUrl);
            }

            if (args.s) {
                let newSource = args.s;
                if (newSource != this.source) {
                    this.localStorage.set('trafficSource', newSource);
                    this.localStorage.set('trafficSourceFirstTime', this.firstTime);

                    this.source = newSource;
                }
            }
        }

        let authCookie = this.cookies.get<any>(environment.auth.cookie);

        if (authCookie && !token)
            token = authCookie.token;

        // Disable localStorage auth token storage for now.
        // It is far too easy for the cookie and the localStorage to disagree.
        // We should just rely on the cookie as the source of truth as it can
        // be read by both the client side and server side apps

        //if (!token)
        //    token = this.localStorage.get<string>('authToken');

        // Install the token (for API etc)
        this.token = token;

        if (token) {
            var jwt = this.decodeToken(token);

            if (!this.justLoggedOut) {
                this.cookies.set(environment.auth.cookie, { token: this.token });
            }

            if (jwt.type === 'User') {
                let user: ApiUser = null;

                if (this.localStorage.get('user')) {
                    user = this.localStorage.get('user');
                }

                if (user)
                    this.user = user;

                if (this.user) {
                    if (!isPlatformServer(this.platformId))
                        Logger.inspect(`Preloaded User`, this.user);
                }

                // Fetch the user
                try {
                    //console.log("Fetching user...");

                    let oldUser = this.user;
                    let newUser = <any>await this.userApi.getCurrentUser().toPromise();

                    if (newUser || !oldUser) {
                        this.user = newUser;
                    }
                } catch (e) {
                    if (e.json)
                        e = e.json();

                    Logger.failure('Failed to fetch user, auth token is likely expired.');
                    Logger.error('Details', e);

                    this.localStorage.remove('user');
                    this.cookies.remove([environment.auth.cookie], environment.auth.domain);

                    // The page should take care of this -- we don't want to send user to the login page when the page doesn't require
                    // login after all

                    this.user = null;
                }

                if (this.user) {
                    if (!this.justLoggedOut) {
                        this.localStorage.set('user', this.user);
                        this.refreshTokenPeriodically();
                    }
                }
            } else if (jwt.type === 'Guest') {
                // Do not enter a signed in state, but do make sure to refresh the token periodically.
                this.refreshTokenPeriodically();
                this.user = null;
            }
        } else {
            // No token, no user.
            this.user = null;
        }

        this.initPrivacyPolicy();
    }

    async initPrivacyPolicy() {
        await this.appConfig.appStatusReady;

        let lastUpdate = this.appConfig.appStatus.settings['privacy_policy_last_updated'];
        let thresholdStr = this.appConfig.appStatus.settings['privacy_policy_threshold_time_days'];
        let thresholdDays = parseInt(thresholdStr || 7);
        let lastUpdateDate: Date;

        if (lastUpdate)
            lastUpdateDate = new Date(lastUpdate);
        else
            lastUpdateDate = new Date('2020-01-01');

        let updateExpiredDate = new Date(lastUpdateDate.getTime() + thresholdDays * 1000 * 60 * 60 * 24);
        let shouldShow = false;

        if (Date.now() < updateExpiredDate.getTime()) {
            shouldShow = true;

            let dismissedDateStr = this.cookies.get<string>('_privacyPolicyDismissed');
            let dismissedDate = new Date(dismissedDateStr);

            if (dismissedDate.getTime() > lastUpdateDate.getTime()) {
                shouldShow = false;
            }

            if (shouldShow) {
                this.shell.addAlert({
                    message: "We've updated our privacy policy. Click for details",
                    id: 'privacy-policy',
                    type: 'info',
                    url: `/privacy`,
                    onDismiss: () => {
                        this.cookies.set('_privacyPolicyDismissed', new Date().toUTCString());
                    }
                });
            }
        }

    }

    resetSource() {
        this.localStorage.remove('trafficSource');
        this.localStorage.remove('trafficSourceFirstTime');
        this.source = null;
    }

    // membershipCanceled() {
    //     // this.user.membership.active = false;
    // }

    billingUpdated(membership: ApiMembership) {
        this.user.membership = membership;
    }

    async completeLoginInPlace(user: ApiUser, token: string) {
        await new Promise<void>(resolve => {
            this.completeLogin(user, token, false, () => resolve());
        });
    }

    saveAuthentication(user: ApiUser, token: string) {
        this.token = token;
        this.localStorage.set('user', user);
        this.localStorage.set('authToken', token);
        this.localStorage.remove('originalToken');
        this.cookies.set(environment.auth.cookie, { token });
        this.user = user;
    }

    completeLogin(user: ApiUser, token: string, hideDialogs = true, defaultAction = null) {
        this.saveAuthentication(user, token);

        if (hideDialogs)
            this.shell.closeAllDialogs();

        setTimeout(() => {
            if (this._postLoginAction) {
                this._postLoginAction();
            } else if (this.postLoginUrl) {
                let destination = this.postLoginUrl;
                this.postLoginUrl = null;
                this.redirection.go(destination);
            } else if (defaultAction) {
                defaultAction();
            } else {
                this.redirection.go('/settings/memberships');
            }
        }, 100);
    }

    private justLoggedOut: boolean = false;

    logout() {
        this.completeLogout();
    }

    completeLogout() {
        this.justLoggedOut = true;
        this.user = null;
        this.token = null;

        this.localStorage.remove('originalToken');

        this.localStorage.remove('user', 'authToken');
        this.cookies.remove([environment.auth.cookie], environment.auth.domain);
    }

    get source() {
        return this._source;
    }

    set source(value: string) {
        this._source = value;
        this.sourceChanged.next(value);
    }

    get loggedIn() {
        return this.user != null && this.token != null;
    }

    userUpdatedAt: number;

    set user(value: ApiUser) {
        this.userUpdatedAt = Date.now();

        if (value && this.isAssumed) {
            console.log('user is assumed');
            value['assumed'] = true;
        }

        this._user = value;
        this.userChanged.next(this._user);
    }

    get isLoggedIn() {
        return this._user != null;
    }

    get user() {
        return this._user;
    }

    get entitled() {
        return this.user && this.user.entitled;
    }

    get audienceType() {

        if (!this.user)
            return 'visitor';

        if (this.user.entitled)
            return 'member';

        if (this.user.membership)
            return 'expired member';

        return 'registered';
    }

    get beaconsEnabled() {
        return environment.settings.enableBeacons || window['force_beacons'] === 1;
    }

    async sendBeacons(label: string, callback: Function) {
        if (typeof window === 'undefined') {
            console.error(`Fatal Error: App requested beacons be sent for '${label}' on the server side! Ensure that beacons are only sent client side! These beacons will be skipped!`);
            return;
        }

        let beaconsEnabled = environment.settings.enableBeacons || window['force_beacons'] === 1;
        if (!beaconsEnabled) {
            console.log(`Analytics: ${label}: Beacons skipped (preproduction)`);
            return;
        }

        console.log(`Analytics: ${label}: Sending beacons...`);
        try {
            await callback();
        } catch (e) {
            console.error(`Caught error while trying to send beacons for event: ${label}`);
            console.error(e);
            console.error(`These beacons will not be retried.`);
        }

        // Make sure enough time is allowed for the beacons to go out.

        await this.timeout(3000);
    }

    sendBeaconFB(eventName: string, contentName: string, metadata: Record<string, any> = {}) {
        const eventId = `${eventName}_${uuid()}`;

        console.log(`FB: Beacon ${eventName}/${contentName}`);
        console.log(`FB EventID ${eventId}`);

        if (typeof window['xfbq'] === 'undefined')
            return;

        let xfbq = window['xfbq'];
        xfbq(
            'track', eventName,
            Object.assign({
                content_name: contentName,
                user_uuid: this.user ? this.user.uuid : null,
            }, {
                ...metadata
            }), {
                eventID: eventId
            }
        );
    }

    sendBeaconGA4(name: string, params: Record<string, any> = {}) {
        if (typeof window === 'undefined')
            return;

        // Notify GA4 directly of the event, because GTM would require us to set up a trigger and tag for every
        // individual event in order to send custom parameters.

        let simulate = !environment.production;
        if (this.localStorage['tyt:send_ga4'] == '1')
            simulate = false;

        console.groupCollapsed(`[GA4${simulate ? ' (Simulated)' : ''}: ${name}`);
        console.dir(params);
        console.groupEnd();

        if (!simulate)
            gtag('event', name, params);
    }

    private timeout(time = 0) {
        return new Promise<void>(resolve => setTimeout(() => resolve(), time));
    }

    goToLogin(returnURL?: string) {
        if (!returnURL)
            returnURL = typeof window !== 'undefined' ? location.href : '/';
        if (returnURL.startsWith('/'))
            returnURL = `${environment.urls.accounts}${returnURL}`;

        this.redirection.go(`${environment.urls.app}/login?${returnURL ? `return=${encodeURIComponent(returnURL)}` : ``}`);
    }

    async saveCommPrefs(optins: ApiNewsletterOptins, provider = 'email') {
        try {
            const patch: ApiCommunicationPref[] = Object.entries(optins).map(
                ([provider_id, allowed]) => ({
                    provider_id,
                    provider,
                    allowed
                })
            );
            await this.commPrefsApi.patchCommunicationPrefs({
                communication_prefs: patch
            }).toPromise();
        } catch (e) {
            console.warn('Failed to save communication preferences');
        }
    }
}
